import React from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { PricingEvent } from '@/store/eventsSlice';
import { Zap, Building } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { getEventLabelsFromEvent, EVENT_CHANGES } from '@/utils/eventLabels';
// ProjectToggleButton removed
import { ScrapbookEntry } from '@/types/scrapbook';
import { useAppSelector } from '@/hooks/useAppSelector';
import { formatVersionDateUS } from '@/utils/dateUtils';

interface EventsListCardProps {
  event: PricingEvent;
  swipefileEntry?: ScrapbookEntry;
  onToggle: (event: PricingEvent) => void;
  onClick: () => void;
}

const EventsListCard = ({
  event,
  swipefileEntry,
  onToggle,
  onClick,
}: EventsListCardProps) => {
  const eventLabels = getEventLabelsFromEvent(event);
  const companies = useAppSelector(state => state.supaCompanies.companies);
  
  // Get company logo from companies store if not available in event
  const company = companies.find(c => c.slug === event.slug);
  const logoUrl = event.logoUrl || company?.logoUrl || '';

  // Calculate quarter from v1 version (YYYYMM format)
  const getQuarter = (version: string) => {
    if (!version || version.length < 6) return version;
    const year = version.substring(0, 4);
    const month = parseInt(version.substring(4, 6), 10);
    if (isNaN(month) || month < 1 || month > 12) return version;
    const quarter = Math.ceil(month / 3);
    return `Q${quarter} ${year}`;
  };

  return (
    <div 
      className="bg-white p-6 shadow-sm hover:shadow-md transition-shadow duration-200 border border-gray-100 hover:bg-gray-50 cursor-pointer relative"
      onClick={onClick}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-start space-x-4">
          <div className="relative w-12 h-12 flex-shrink-0">
            {logoUrl ? (
              <img
                src={logoUrl}
                alt={`${event.slug} logo`}
                className="w-full h-full object-contain"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gray-100 rounded-md">
                <Building className="h-6 w-6 text-gray-400" />
              </div>
            )}
            <div className="absolute -bottom-1 -right-1 bg-white rounded-full p-0.5 shadow-sm border-2 border-white">
              <Zap className="h-4 w-4 text-yellow-500" />
            </div>
          </div>
          <div>
            <h3 className="text-lg font-semibold capitalize">
              {event.slug.replace(/-/g, ' ')}
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              {event.description}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              Changed in {getQuarter(event.v1)}
            </p>
            <div className="flex flex-wrap gap-2 mt-2">
              {eventLabels.map((label, index) => {
                const labelKey = Object.keys(EVENT_CHANGES).find(
                  key => EVENT_CHANGES[key].label === label.label
                );
                return (
                  <Badge
                    key={index}
                    variant="secondary"
                    className={`flex items-center gap-1 ${labelKey ? EVENT_CHANGES[labelKey].className : ''}`}
                  >
                    {label.icon && <label.icon className="h-3 w-3" />}
                    {label.label}
                  </Badge>
                );
              })}
            </div>
          </div>
        </div>
        {/* Project toggle removed */}
      </div>
    </div>
  );
};

export default EventsListCard;